import type { Client, Vendor } from "@/types";
import { Box, Grid2 as Grid, Typography, Divider, Link, Stack } from "@mui/material";
import { useRecordContext } from "react-admin";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import { SectionCard } from "@/components/common/SectionCard";

interface ClientContactsCardProps {
  carrierVendor?: Vendor;
  tpaVendor?: Vendor;
  rxVendor?: Vendor;
  /** When true, renders content without the SectionCard wrapper (for use inside another card). */
  embedded?: boolean;
}

export const ClientContactsCard = ({
  carrierVendor,
  tpaVendor,
  rxVendor,
  embedded = false,
}: ClientContactsCardProps) => {
  const client = useRecordContext<Client>();
  if (!client) return null;

  const formatContact = (vendor?: Vendor) => {
    if (!vendor?.claims_contact) return null;

    const { name, email, phone, phone_ext } = vendor.claims_contact;
    if (!name && !email && !phone) return null;

    const phoneStr = phone ? (phone_ext ? `${phone} ext. ${phone_ext}` : `${phone}`) : null;

    return {
      name: name || null,
      phone: phoneStr,
      email: email || null,
    };
  };

  const formatClientContact = () => {
    const contacts = client?.contacts || [];
    if (contacts.length === 0) return null;

    // Use primary_contact index, fall back to first contact
    const primaryIndex = client?.primary_contact ?? 0;
    const primaryContact = contacts[primaryIndex] || contacts[0];
    if (!primaryContact) return null;

    const { contact_name, email, phone } = primaryContact;
    if (!contact_name && !email && !phone) return null;

    return {
      name: contact_name || null,
      phone: phone || null,
      email: email || null,
    };
  };

  const clientContact = formatClientContact();
  const carrierContact = formatContact(carrierVendor);
  const tpaContact = formatContact(tpaVendor);
  const rxContact = formatContact(rxVendor);

  const contactRows = [];
  if (clientContact) contactRows.push({ label: "Client", ...clientContact });
  if (carrierContact) contactRows.push({ label: "Carrier", ...carrierContact });
  if (tpaContact) contactRows.push({ label: "TPA", ...tpaContact });
  if (rxContact) contactRows.push({ label: "Rx Vendor", ...rxContact });

  if (contactRows.length === 0) return null;

  const content = (
    <Grid container spacing={{ xs: 2, sm: 2.5 }}>
      {contactRows.map((row, idx) => (
        <Grid key={idx} size={{ xs: 12 }}>
          <Box>
            <Typography variant="body2" color="text.secondary" fontWeight={600} sx={{ mb: 0.5 }}>
              {row.label}
            </Typography>
            <Stack spacing={0.5}>
              {row.name && <Typography variant="body1">{row.name}</Typography>}
              <Stack
                direction={{ xs: "column", lg: "row" }}
                spacing={{ xs: 0.5, lg: 1.5 }}
                alignItems={{ xs: "flex-start", lg: "center" }}
                flexWrap="wrap"
              >
                {row.email && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      minWidth: 0,
                    }}
                  >
                    <EmailIcon
                      fontSize="small"
                      sx={{
                        color: "text.secondary",
                        flexShrink: 0,
                      }}
                    />
                    <Link
                      href={`mailto:${row.email}`}
                      variant="body2"
                      sx={{
                        textDecoration: "none",
                        wordBreak: "break-word",
                        overflowWrap: "break-word",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      {row.email}
                    </Link>
                  </Box>
                )}
                {row.phone && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <PhoneIcon
                      fontSize="small"
                      sx={{
                        color: "text.secondary",
                        flexShrink: 0,
                      }}
                    />
                    <Link
                      href={`tel:${row.phone}`}
                      variant="body2"
                      sx={{
                        textDecoration: "none",
                        "&:hover": { textDecoration: "underline" },
                      }}
                    >
                      {row.phone}
                    </Link>
                  </Box>
                )}
              </Stack>
            </Stack>
          </Box>
          {idx < contactRows.length - 1 && <Divider sx={{ mt: { xs: 2, sm: 2.5 } }} />}
        </Grid>
      ))}
    </Grid>
  );

  if (embedded) {
    return (
      <Box>
        <Typography variant="overline" color="text.secondary" sx={{ mb: 1.5, display: "block" }}>
          Contacts
        </Typography>
        {content}
      </Box>
    );
  }

  return (
    <SectionCard title="Contacts" subtitle="Key partners for this stop-loss policy">
      {content}
    </SectionCard>
  );
};
